// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { addDays, format, startOfToday, subDays } from "date-fns";
import type { StoreGoLiveStatus } from "@/api/hooks/use-stores";
import type { DateRangeFilterValue } from "@/components/shared/filter-date-range";

/** Query params a chosen date-filter option stands for (go-live or install date). */
export interface DateFilterParams {
  status?: StoreGoLiveStatus;
  from?: string;
  to?: string;
}

function isoDay(date: Date): string {
  // The emitted *value*, not a display format — never localised.
  return format(date, "yyyy-MM-dd");
}

/**
 * The named date-filter options, each resolved to query params at fetch time rather than at click
 * time. That's what keeps "next 30 days" rolling: only the option id is stored in the URL and in
 * saved presets, so a preset saved in March still means "the next 30 days" when replayed in July.
 *
 * `live` / `upcoming` are deliberately left to the server, which compares against each store's own
 * local date — a chain spanning time zones would otherwise flip a store a day early or late.
 *
 * Go-live and install dates share the ids and resolution; only the labels differ.
 */
function dateFilterOptions(labelPrefix: string) {
  return [
    { value: "live", labelKey: `${labelPrefix}.live`, resolve: (): DateFilterParams => ({ status: "live" }) },
    {
      value: "upcoming",
      labelKey: `${labelPrefix}.upcoming`,
      resolve: (): DateFilterParams => ({ status: "upcoming" }),
    },
    {
      value: "next30",
      labelKey: `${labelPrefix}.next30`,
      resolve: (): DateFilterParams => ({ from: isoDay(startOfToday()), to: isoDay(addDays(startOfToday(), 30)) }),
    },
    {
      value: "last30",
      labelKey: `${labelPrefix}.last30`,
      resolve: (): DateFilterParams => ({ from: isoDay(subDays(startOfToday(), 30)), to: isoDay(startOfToday()) }),
    },
    { value: "set", labelKey: `${labelPrefix}.set`, resolve: (): DateFilterParams => ({ status: "set" }) },
    { value: "notSet", labelKey: `${labelPrefix}.notSet`, resolve: (): DateFilterParams => ({ status: "notSet" }) },
  ] as const;
}

export const GO_LIVE_OPTIONS = dateFilterOptions("stores.list.filter.goLive");
export const INSTALL_OPTIONS = dateFilterOptions("stores.list.filter.install");

/** Turns the widget's value into `/v1/stores` query params. A named option always wins over a span. */
export function resolveDateFilter(value: DateRangeFilterValue): DateFilterParams {
  // Both option sets share ids and resolvers, so either serves for lookup.
  const option = GO_LIVE_OPTIONS.find((o) => o.value === value.option);
  if (option) return option.resolve();
  return { from: value.from, to: value.to };
}
